import React, { useEffect, useMemo, useState } from "react";
import { View, TouchableOpacity, ScrollView, StyleSheet } from "react-native";
import Text from "@components/CustomText";
import BottomSheet from "@components/ds/BottomSheet";
import SheetButtons from "@components/ds/SheetButtons";
import { tokens } from "../styles";

// Inventory marking sheet — per-SKU stepper for damaged / wrong-item /
// not-found counts. Total can't exceed the SKU's unpacked qty. When the
// caller passes a `sku.unpacked_order_item_ids` array (e.g. the
// customer-scoped per-cx entry), the stepper caps to that array's length
// and the submit payload includes `order_item_ids` so the server scopes
// marking to that customer's items only.

const REASONS = [
  { key: "item_damaged", label: "Damaged" },
  { key: "wrong_item_received", label: "Wrong Item" },
  { key: "not_found", label: "Missing" },
];

const InventoryMarkSheet = ({
  visible,
  sku,
  onClose,
  onSubmit,
  submitting,
}) => {
  const [diff, setDiff] = useState({});

  useEffect(() => {
    if (!visible) return;
    const init = {};
    REASONS.forEach((r) => {
      init[r.key] = 0;
    });
    setDiff(init);
  }, [visible]);

  // Prefer the explicit per-customer array length when present; the
  // legacy `unpacked_qty` is sku-aggregate and would over-cap when the
  // sheet is opened for a single customer's slice.
  const scopedIds = useMemo(
    () =>
      Array.isArray(sku?.unpacked_order_item_ids)
        ? sku.unpacked_order_item_ids
        : null,
    [sku],
  );
  const available = scopedIds
    ? scopedIds.length
    : Number(sku?.unpacked_qty || 0);
  const total = useMemo(
    () => Object.values(diff).reduce((sum, n) => sum + Number(n || 0), 0),
    [diff],
  );
  const exceeds = total > available;
  const blocked = total === 0 || exceeds || !sku;

  const bump = (key, delta) => {
    setDiff((prev) => {
      const current = Number(prev[key] || 0);
      const candidate = current + delta;
      if (candidate < 0) return prev;
      if (delta > 0 && total + delta > available) return prev;
      return { ...prev, [key]: candidate };
    });
  };

  const handleConfirm = () => {
    if (blocked || !sku) return;
    onSubmit({
      sku_id: sku.sku_id,
      diff,
      ...(scopedIds && scopedIds.length > 0
        ? { order_item_ids: scopedIds }
        : {}),
    });
  };

  return (
    <BottomSheet
      visible={visible}
      title="Mark Inventory Issue"
      subtitle={
        sku?.cx_name
          ? `${sku.cx_name} · ${sku?.sku_name || sku?.sku_id || ""}`
          : sku?.sku_name || sku?.sku_id || ""
      }
      onClose={onClose}
      footer={
        <SheetButtons
          onCancel={onClose}
          onConfirm={handleConfirm}
          loading={submitting}
          disabled={blocked}
          confirmLabel="Submit"
        />
      }
    >
      <ScrollView keyboardShouldPersistTaps="handled" style={styles.scroll}>
        {sku ? (
          <Text variant="caption_2" style={styles.summary}>
            {available} unit{available === 1 ? "" : "s"} unpacked
            {sku?.cx_name
              ? ` · ${sku.cx_name}`
              : sku?.cx_count
                ? ` · ${sku.cx_count} customer${
                    Number(sku.cx_count) === 1 ? "" : "s"
                  }`
                : ""}
          </Text>
        ) : null}
        {REASONS.map((r) => {
          const value = Number(diff[r.key] || 0);
          return (
            <View key={r.key} style={styles.reasonRow}>
              <Text variant="caption_2" style={styles.reasonLabel}>
                {r.label}
              </Text>
              <View style={styles.stepper}>
                <TouchableOpacity
                  activeOpacity={0.85}
                  onPress={() => bump(r.key, -1)}
                  disabled={value === 0}
                  style={[
                    styles.stepBtn,
                    value === 0 && styles.stepBtnDisabled,
                  ]}
                >
                  <Text variant="caption_2" style={styles.stepText}>
                    −
                  </Text>
                </TouchableOpacity>
                <Text variant="caption_2" style={styles.stepValue}>
                  {value}
                </Text>
                <TouchableOpacity
                  activeOpacity={0.85}
                  onPress={() => bump(r.key, +1)}
                  disabled={total >= available}
                  style={[
                    styles.stepBtn,
                    total >= available && styles.stepBtnDisabled,
                  ]}
                >
                  <Text variant="caption_2" style={styles.stepText}>
                    +
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          );
        })}
        <Text variant="caption_2" style={styles.totals}>
          Marked: {total} / {available}
        </Text>
        {exceeds ? (
          <Text variant="caption_2" style={styles.error}>
            Cannot exceed unpacked quantity.
          </Text>
        ) : null}
      </ScrollView>
    </BottomSheet>
  );
};

export default InventoryMarkSheet;

const styles = StyleSheet.create({
  scroll: { maxHeight: 360 },
  summary: {
    color: tokens.textSecondary,
    fontSize: 12,
    marginBottom: tokens.spacing.medium,
  },
  reasonRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: tokens.spacing.small,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: tokens.borderLight,
  },
  reasonLabel: {
    flex: 1,
    color: tokens.textPrimary,
    fontSize: 13,
    fontWeight: "600",
  },
  stepper: {
    flexDirection: "row",
    alignItems: "center",
  },
  stepBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: tokens.borderMedium,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: tokens.surface,
  },
  stepBtnDisabled: {
    borderColor: tokens.borderLight,
    backgroundColor: tokens.bg,
  },
  stepText: { color: tokens.textPrimary, fontSize: 16, fontWeight: "700" },
  stepValue: {
    minWidth: 32,
    textAlign: "center",
    color: tokens.textPrimary,
    fontSize: 14,
    fontWeight: "700",
    marginHorizontal: tokens.spacing.small,
  },
  totals: {
    color: tokens.textSecondary,
    fontSize: 12,
    marginTop: tokens.spacing.medium,
    textAlign: "right",
  },
  error: {
    color: tokens.warning,
    fontSize: 12,
    marginTop: tokens.spacing.tiny,
    textAlign: "right",
  },
});
